import type {
  FbsClientFilesResponse,
  FbsEmailsResponse,
  FbsFilterOptionsResponse,
  FbsListQueryParams,
  FbsListResponse,
  FbsShareResponse,
  FbsUnpaidInvoicesResponse,
  GenerateFbsRequest,
  GenerateFbsResponse,
} from '@/types/fbs.types';
import { useMutation, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import axiosInstance from './axiosInstance';
import { toast } from '@/components/ui/use-toast';
import { downloadFilefromBuffer } from '@/lib/utils';
import type { AxiosError } from 'axios';
import type { GenericErrorResponse } from '@/types/api.types';

const serializeArrayParam = (value?: string | string[]) => {
  if (!value) return undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? JSON.stringify([trimmed]) : undefined;
  }
  return value.length ? JSON.stringify(value) : undefined;
};

export const getFbsListData = async (params: FbsListQueryParams): Promise<FbsListResponse> => {
  const response = await axiosInstance.get<FbsListResponse>('/final-billing-summary/list', {
    params: {
      ...params,
      code: serializeArrayParam(params.code),
      fileNumber: serializeArrayParam(params.fileNumber),
    },
  });
  return response.data;
};

export const getFbs = (params: FbsListQueryParams, options?: { query?: Partial<UseQueryOptions<FbsListResponse>> }) => {
  const query = useQuery<FbsListResponse>({
    queryFn: () => getFbsListData(params),
    ...options?.query,
    queryKey: ['fbs', params, ...(options?.query?.queryKey ?? [])],
  });

  return {
    data: query.data?.items ?? [],
    totalRows: query.data?.totalRows ?? 0,
    isFetching: query.isFetching,
  };
};

export const getFbsFilterOptions = async () => {
  const response = await axiosInstance.get<FbsFilterOptionsResponse>('/final-billing-summary/filter-options');
  return response.data;
};

export const useFbsFilterOptions = () => {
  return useQuery<FbsFilterOptionsResponse>({
    queryKey: ['fbs-filter-options'],
    queryFn: getFbsFilterOptions,
    staleTime: 1000 * 60 * 2,
  });
};

export const getClientFilesForFbs = async (clientId: string) => {
  const response = await axiosInstance.get<FbsClientFilesResponse>('/final-billing-summary/client-files', {
    params: { clientId },
  });
  return response.data;
};

export const useClientFilesForFbs = (clientId: string | undefined) => {
  return useQuery<FbsClientFilesResponse>({
    queryKey: ['fbs-client-files', clientId],
    queryFn: () => getClientFilesForFbs(clientId!),
    enabled: Boolean(clientId),
    staleTime: 1000 * 60,
  });
};

export const generateFbs = async (body: GenerateFbsRequest) => {
  const response = await axiosInstance.post<GenerateFbsResponse>('/final-billing-summary/generate', body, {
    timeout: 5 * 60 * 1000,
  });
  return response.data;
};

export const useGenerateFbsMutation = () => {
  return useMutation<GenerateFbsResponse, AxiosError<GenericErrorResponse>, GenerateFbsRequest>({
    mutationFn: generateFbs,
  });
};

export const exportFbsZip = async (fbsIds: string[]) => {
  toast({
    description: 'Preparing Final Billing Summary export...',
    duration: 3000,
  });
  try {
    const response = await axiosInstance.post<Blob>(
      '/final-billing-summary/export',
      { fbsIds },
      {
        responseType: 'blob',
        timeout: 5 * 60 * 1000,
      }
    );
    const fileName = fbsIds.length === 1 ? 'final_billing_summary.zip' : 'final_billing_summaries.zip';
    downloadFilefromBuffer(response.data, fileName, 'ZIP');
  } catch {
    toast({
      description: 'Unable to export Final Billing Summary, please try again',
      duration: 3000,
      variant: 'destructive',
    });
  }
};

export const getFbsEmails = async (fbsIds: string[]): Promise<FbsEmailsResponse> => {
  const response = await axiosInstance.get<FbsEmailsResponse>('/final-billing-summary/emails', {
    params: { fbsIds: serializeArrayParam(fbsIds) },
  });
  return response.data;
};

export const useShareFbsMutation = () => {
  return useMutation<FbsShareResponse, AxiosError<GenericErrorResponse>, { fbsIds: string[] }>({
    mutationFn: async ({ fbsIds }) => {
      const response = await axiosInstance.post<FbsShareResponse>(
        '/final-billing-summary/share',
        { fbsIds },
        {
          timeout: 5 * 60 * 1000,
        }
      );
      return response.data;
    },
  });
};

export const getFbsUnpaidInvoices = async (fbsId: string) => {
  const response = await axiosInstance.get<FbsUnpaidInvoicesResponse>(
    `/final-billing-summary/${fbsId}/unpaid-invoices`
  );
  return response.data;
};

export const useFbsUnpaidInvoices = (fbsId: string | undefined, enabled = false) => {
  return useQuery<FbsUnpaidInvoicesResponse>({
    queryKey: ['fbs-unpaid-invoices', fbsId],
    queryFn: () => getFbsUnpaidInvoices(fbsId!),
    enabled: Boolean(fbsId) && enabled,
  });
};
